import React, { useEffect } from "react";
import { Route, Routes, useNavigate } from "react-router-dom";
import { Bounce, ToastContainer } from "react-toastify";
import { SplashScreen } from "@capacitor/splash-screen";
import { LoadScript } from "@react-google-maps/api";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useDarkMode } from "usehooks-ts";
import hotjar from "@hotjar/browser";

import LoadingIndicator from "components/LoadingSpinner";
import ProtectedRoute from "components/Routes/Protected";
import PublicRoute from "components/Routes/Public";
import { libraries } from "constants/google-map";
import { routes } from "constants/routes";
import ResetPassword from "features/auth/ResetPassword";
import SignIn from "features/auth/SignIn";
import SignUp from "features/auth/SignUp";
import OtherUserProfile from "features/main/UserProfile/Other";
import ApplyHaulpay from "features/onboarding/ApplyHaulpay";
import InvitedCarrierOnboarding from "features/onboarding/InvitedCarrier";
import SearchFactor from "features/onboarding/InvitedCarrier/SearchFactor";
import useCheckAccount from "hooks/useCheckAccount";
import { useColorScheme } from "hooks/useColorScheme";
import useFirebase, { IFirebaseReturn } from "hooks/useFirebase";
import useGATracking from "hooks/useGATracking";
import usePushNotifications from "./hooks/usePushNotifications";
import AppLayout from "layouts/App";
import HaulPayRoutes from "routes/haulpay/HaulPayRoutes";
import EnterNewPassword from "./features/auth/EnterNewPassword";

import HomeRoutes from "routes/home/HomeRoutes";
import LoadsRoutes from "routes/loads/LoadsRoutes";
import RateTrendRoutes from "routes/rate-trend/RateTrendRoutes";
import SavedPostsRoutes from "routes/SavedPosts/SavedPostsRoutes";
import SettingsRoutes from "routes/settings/SettingsRoutes";
import TrucksRoutes from "routes/trucks/TrucksRoutes";
import RedirectRoutes from "./routes/RedirectRoutes";

import "react-toastify/dist/ReactToastify.css";
import "swiper/css";
import "swiper/css/autoplay";
import "swiper/css/effect-fade";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "./App.scss";
import NotFound from "./features/404";
import DefaultRedirect from "./components/DefaultRedirect"; // the default redirect component
import { setGlobalNavigate } from "./services/NavigationService";
import useHotjarTracking from "./hooks/useHotjarTracking";
import useHubspot from "./hooks/useHubspot";
import { SafeAreaProvider } from "./providers/SafeAreaProvider";

dayjs.extend(utc);

function App() {
  const { set } = useDarkMode();
  const navigate = useNavigate();

  useColorScheme();
  useGATracking();
  const { messaging }: IFirebaseReturn = useFirebase();
  usePushNotifications(messaging);
  useCheckAccount();

  const initializeApp = async () => {
    await new Promise((resolve) => setTimeout(resolve, 1000));
    SplashScreen.hide();
  };

  useEffect(() => {
    setGlobalNavigate(navigate);
  }, [navigate]);

  useEffect(() => {
    set(true);
    process.env.REACT_APP_HOTJAR_ID && hotjar.init(Number(process.env.REACT_APP_HOTJAR_ID), 6);
    initializeApp();
  }, []);

  useHubspot();
  useHotjarTracking();

  return (
    <SafeAreaProvider>
      <LoadScript
        googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAP_API_KEY ?? ""}
        libraries={libraries}>
        <LoadingIndicator />
        <AppLayout>
          <Routes>
            {/* Default route: redirect conditionally based on authentication */}
            <Route path="/" element={<DefaultRedirect />} />

            {/* Public Routes */}
            <Route element={<PublicRoute />}>
              <Route path="/login" element={<SignIn />} />
              <Route path={routes.SIGNIN} element={<SignIn />} />
              <Route path={routes.RESET_PASSWORD} element={<ResetPassword />} />
              <Route path={routes.SIGNUP} element={<SignUp />} />
              <Route path={routes.ENTER_NEW_PASSWORD} element={<EnterNewPassword />} />
            </Route>

            {/* Specific Routes */}
            <Route path="signup/:userType" element={<ApplyHaulpay />} />
            <Route path="invited/:inviteToken" element={<InvitedCarrierOnboarding />} />
            <Route path="invited/:inviteToken/search" element={<SearchFactor />} />

            {/* Protected Routes */}
            <Route element={<ProtectedRoute />}>
              <Route path="/finance/apply" element={<ApplyHaulpay />} />
              {HomeRoutes()}
              {HaulPayRoutes()}
              {TrucksRoutes()}
              {LoadsRoutes()}
              {RateTrendRoutes()}
              {SavedPostsRoutes()}
              <Route path={routes.USER}>
                <Route path=":userId" element={<OtherUserProfile />} />
              </Route>
              {SettingsRoutes()}
              {RedirectRoutes()}
            </Route>

            {/* Wildcard route for any unmatched paths */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </AppLayout>
        <ToastContainer
          position="bottom-right"
          icon={false}
          hideProgressBar={true}
          autoClose={3000}
          transition={Bounce}
          closeOnClick
          pauseOnHover={false}
        />
      </LoadScript>
    </SafeAreaProvider>
  );
}

export default App;
